'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { RotateCwIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Response } from '@/components/elements/response'
import { SparkleIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DatapointCard,
  DatapointNoteProvider,
  DatapointSummaryProvider,
} from '@/features/home/components/home-datapoint-cards'
import { HomeGauges } from '@/features/home/components/home-gauges'
import { HOME_EASE, HOME_LIST, HomePanel, LiveDot } from '@/features/home/components/home-motion'
import type { HomeDailyBrief } from '@/features/home/hooks/use-home-daily-brief'
import { useRelativeTime } from '@/features/home/hooks/use-relative-time'
import { BRIEF_SIZE } from '@/features/home/lib/home-datapoints'
import type { Gauge } from '@/features/home/lib/home-gauges'
import { useNow } from '@/hooks/use-now'
import { cn } from '@/lib/utils'

/** "funding_rates" → "Funding rates": the research run's tool names, readable. */
function stepLabel(tool: string): string {
  const words = tool.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function startOfToday(now: number): number {
  const day = new Date(now)
  day.setHours(0, 0, 0, 0)
  return day.getTime()
}

/** The AI's comment on the datapoints: write it, watch it being written, read it, refresh it. */
function BriefComment({ brief }: { brief: HomeDailyBrief }) {
  const t = useTranslations('trade.home.brief.ai')
  const relative = useRelativeTime()
  const now = useNow(1000)
  const { ai } = brief
  const stale = ai.status === 'done' && (ai.finishedAt ?? 0) < startOfToday(now)
  const elapsed = ai.requestedAt ? Math.max(0, Math.floor((now - ai.requestedAt) / 1000)) : 0

  return (
    // On a wide screen the cards set the row's height and the comment lives inside it (absolute),
    // so a long answer scrolls in place instead of stretching every card beside it.
    <div className="bg-foreground/[0.03] border-foreground/10 relative min-h-64 overflow-hidden rounded-md border">
      <div className="flex h-full flex-col p-4 lg:absolute lg:inset-0">
        <div className="mb-3 flex shrink-0 items-center gap-2">
          <motion.span
            className="text-shell-accent flex"
            animate={ai.status === 'running' ? { rotate: 360 } : { rotate: 0 }}
            transition={
              ai.status === 'running'
                ? { duration: 2.4, repeat: Infinity, ease: 'linear' }
                : { duration: 0.3 }
            }
          >
            <SparkleIcon className="size-3.5" />
          </motion.span>
          <span className="text-foreground text-sm font-medium tracking-tight">{t('title')}</span>
          {ai.status === 'done' && ai.finishedAt && (
            <span
              className={cn(
                'ml-auto rounded-full px-1.5 py-0.5 text-[10px]',
                stale ? 'bg-ox-gold/10 text-ox-gold' : 'text-foreground/40'
              )}
            >
              {stale
                ? t('stale')
                : t('updated', {
                    time: new Date(ai.finishedAt).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                    ago: relative(ai.finishedAt),
                  })}
            </span>
          )}
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {ai.status === 'running' ? (
            <motion.div
              key="running"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: HOME_EASE }}
              className="flex flex-1 flex-col gap-3"
            >
              {[0.92, 0.78, 0.86, 0.6].map((width, index) => (
                <motion.span
                  key={index}
                  className="bg-foreground/10 h-3 rounded-full"
                  style={{ width: `${width * 100}%` }}
                  animate={{ opacity: [0.35, 0.8, 0.35] }}
                  transition={{ duration: 1.6, repeat: Infinity, delay: index * 0.15 }}
                />
              ))}
              <ul className="mt-1 flex flex-col gap-1">
                <AnimatePresence initial={false}>
                  {ai.steps.slice(-3).map((step, index, list) => (
                    <motion.li
                      key={`${step}-${index}`}
                      layout
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: index === list.length - 1 ? 1 : 0.5, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-foreground/60 flex items-center gap-2 text-xs"
                    >
                      {index === list.length - 1 ? (
                        <LiveDot className="text-shell-accent" />
                      ) : (
                        <span className="bg-foreground/30 size-1.5 rounded-full" />
                      )}
                      {stepLabel(step)}
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
              <p className="text-foreground/40 mt-auto text-xs">
                {t('running', { seconds: elapsed })}
              </p>
            </motion.div>
          ) : ai.status === 'done' && ai.parsed ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: stale ? 0.6 : 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: HOME_EASE }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 overflow-y-auto">
                {ai.parsed.sections ? (
                  // The fixed skeleton: what changed → what it means for you → what to watch.
                  <dl className="flex flex-col gap-2.5">
                    {(['changed', 'meaning', 'watch'] as const).map((key, index) =>
                      ai.parsed?.sections?.[key] ? (
                        <motion.div
                          key={key}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.08, duration: 0.25, ease: HOME_EASE }}
                          className="flex flex-col gap-0.5"
                        >
                          <dt className="text-shell-accent text-[10px] font-medium tracking-wide uppercase">
                            {t(`sections.${key}`)}
                          </dt>
                          <dd className="text-foreground/85 text-sm leading-6">
                            {ai.parsed.sections[key]}
                          </dd>
                        </motion.div>
                      ) : null
                    )}
                  </dl>
                ) : (
                  <Response className="text-foreground/85 text-sm leading-6 [&_p]:mb-2">
                    {ai.parsed.brief}
                  </Response>
                )}
              </div>
              {ai.sources.length > 0 && (
                <div className="flex shrink-0 flex-wrap items-center gap-1 pt-2">
                  <span className="text-foreground/35 text-[10px]">{t('sources')}</span>
                  {ai.sources.slice(0, 4).map((source) => (
                    <span
                      key={source}
                      className="bg-foreground/5 text-foreground/55 rounded-xs px-1.5 py-px font-mono text-[10px]"
                    >
                      {source}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-foreground/35 shrink-0 pt-2 text-[10px]">{t('disclaimer')}</p>
              <div className="flex shrink-0 flex-wrap items-center gap-1 pt-3">
                <Button variant="secondary" size="sm" onClick={() => brief.setFullBriefOpen(true)}>
                  {t('readFull')}
                </Button>
                <Button
                  variant={stale ? 'default' : 'ghost'}
                  size="sm"
                  onClick={brief.writeBrief}
                  className="group"
                >
                  <RotateCwIcon className="size-3.5 transition-transform duration-500 ease-(--ease-spring) group-hover:rotate-180" />
                  {t(stale ? 'writeToday' : 'refresh')}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: HOME_EASE }}
              className="flex flex-1 flex-col items-start justify-center gap-3"
            >
              <p className="text-foreground/70 text-sm leading-6">
                {ai.status === 'failed' ? t('failed') : t('idle')}
              </p>
              <Button size="sm" onClick={brief.writeBrief} className="group">
                <SparkleIcon className="size-3.5 transition-transform duration-500 ease-(--ease-spring) group-hover:rotate-90" />
                {t(ai.status === 'failed' ? 'retry' : 'write')}
              </Button>
              <p className="text-foreground/40 text-[11px]">{t('cost')}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/**
 * Today's brief: the Data widget's numbers that matter for what the user holds — picked per
 * position (implied move vs liquidation, funding on a held perp, IV for option holders, ETF
 * flows, the Fed) — with the AI's comment on them for these positions.
 */
export function HomeBrief({
  brief,
  gauges,
  className,
}: {
  brief: HomeDailyBrief
  /** Topic gauges from Home's live market read; hidden until there is something to show. */
  gauges?: readonly Gauge[]
  className?: string
}) {
  const t = useTranslations('trade.home.brief')
  const held = brief.exposures.some((exposure) => exposure.positions.length > 0)

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        brief.ready && (
          <span className="text-foreground/45 hidden text-xs sm:inline">
            {held ? t('subtitle') : t('subtitleWatch')}
          </span>
        )
      }
    >
      {gauges && gauges.some((gauge) => gauge.score !== null) && (
        <HomeGauges gauges={gauges} className="mb-3" />
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {!brief.ready ? (
          // Same grid and card height as the real picks, so nothing jumps when they land.
          <ul
            className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-2"
            aria-busy="true"
          >
            {Array.from({ length: BRIEF_SIZE }, (_, index) => (
              <li
                key={index}
                className="border-foreground/10 bg-foreground/[0.03] flex h-40 flex-col gap-2 rounded-md border p-3"
              >
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-3 w-40" />
              </li>
            ))}
          </ul>
        ) : (
          <DatapointSummaryProvider value={brief.reportSummary}>
            <DatapointNoteProvider value={brief.ai.noteFor}>
              <motion.ul
                variants={HOME_LIST}
                initial="hidden"
                animate="show"
                className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-2"
              >
                {/* A hidden card leaves and the next pick slides into its place. */}
                <AnimatePresence mode="popLayout">
                  {brief.datapoints.map((datapoint) => (
                    <DatapointCard
                      key={datapoint.id}
                      datapoint={datapoint}
                      exposure={brief.exposures.find(
                        (exposure) => exposure.asset === datapoint.asset
                      )}
                      ticker={brief.tickerOf(datapoint.perp?.symbol)}
                      instrument={brief.instrumentOf(datapoint.perp)}
                      onHide={() => brief.hideKind(datapoint.kind)}
                    />
                  ))}
                </AnimatePresence>
              </motion.ul>
            </DatapointNoteProvider>
          </DatapointSummaryProvider>
        )}
        <BriefComment brief={brief} />
      </div>
      {brief.hiddenCount > 0 && (
        <p className="text-foreground/40 pt-3 text-xs">
          {t('hiddenCount', { count: brief.hiddenCount })}{' '}
          <button
            type="button"
            onClick={brief.showAllKinds}
            className="text-foreground/60 hover:text-foreground cursor-pointer underline-offset-2 transition-colors hover:underline"
          >
            {t('showAll')}
          </button>
        </p>
      )}
      <Dialog open={brief.fullBriefOpen} onOpenChange={brief.setFullBriefOpen}>
        <DialogContent className="max-h-[calc(100dvh-4rem)] max-w-3xl overflow-y-auto p-8">
          <DialogTitle className="flex items-center gap-2 text-lg tracking-tight">
            <SparkleIcon className="text-shell-accent size-4" />
            {t('ai.fullTitle')}
          </DialogTitle>
          <Response className="text-foreground/85 text-sm leading-6 [&_p]:mb-3 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto">
            {brief.ai.parsed?.full ?? ''}
          </Response>
        </DialogContent>
      </Dialog>
    </HomePanel>
  )
}
